import React, { createContext, useContext, useEffect, useState } from "react";

const STORAGE_KEY = "rieave.cart";
const SAVED_KEY = "rieave.saved";
const CartContext = createContext(null);

const read = (key) => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : [];
  } catch { return []; }
};

const write = (key, items) => {
  try { localStorage.setItem(key, JSON.stringify(items)); } catch {}
};

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(() => read(STORAGE_KEY));
  const [savedItems, setSavedItems] = useState(() => read(SAVED_KEY));
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [giftProductId, setGiftProductId] = useState(() => {
    try { return localStorage.getItem("rieave.gift") || null; } catch { return null; }
  });

  useEffect(() => { write(STORAGE_KEY, items); }, [items]);
  useEffect(() => { write(SAVED_KEY, savedItems); }, [savedItems]);
  useEffect(() => {
    try {
      if (giftProductId) localStorage.setItem("rieave.gift", giftProductId);
      else localStorage.removeItem("rieave.gift");
    } catch {}
  }, [giftProductId]);

  const addItem = (product, quantity = 1, size = null) => {
    setItems((prev) => {
      const idx = prev.findIndex((i) => i.product_id === product.id && i.size === size);
      if (idx >= 0) {
        const cp = [...prev];
        cp[idx] = { ...cp[idx], quantity: cp[idx].quantity + quantity };
        return cp;
      }
      return [
        ...prev,
        {
          product_id: product.id,
          slug: product.slug,
          name: product.name,
          price: product.price,
          compare_at_price: product.compare_at_price,
          image: (product.images || [])[0] || "",
          quantity,
          size,
        },
      ];
    });
    setDrawerOpen(true);
  };

  const removeItem = (product_id, size = null) => {
    setItems((prev) => prev.filter((i) => !(i.product_id === product_id && i.size === size)));
  };

  const updateQty = (product_id, size, quantity) => {
    setItems((prev) => prev.map((i) => (i.product_id === product_id && i.size === size ? { ...i, quantity: Math.max(1, quantity) } : i)));
  };

  // ---- Saved for later ----
  const saveForLater = (product_id, size = null) => {
    const target = items.find((i) => i.product_id === product_id && i.size === size);
    if (!target) return;
    setSavedItems((prev) => {
      if (prev.some((s) => s.product_id === product_id && s.size === size)) return prev;
      return [{ ...target, quantity: 1 }, ...prev];
    });
    removeItem(product_id, size);
  };

  const moveSavedToCart = (product_id, size = null) => {
    const target = savedItems.find((i) => i.product_id === product_id && i.size === size);
    if (!target) return;
    setItems((prev) => {
      const idx = prev.findIndex((i) => i.product_id === product_id && i.size === size);
      if (idx >= 0) {
        const cp = [...prev];
        cp[idx] = { ...cp[idx], quantity: cp[idx].quantity + 1 };
        return cp;
      }
      return [...prev, { ...target, quantity: 1 }];
    });
    setSavedItems((prev) => prev.filter((i) => !(i.product_id === product_id && i.size === size)));
  };

  const removeSaved = (product_id, size = null) => {
    setSavedItems((prev) => prev.filter((i) => !(i.product_id === product_id && i.size === size)));
  };

  const clear = () => { setItems([]); setGiftProductId(null); };

  const subtotal = items.reduce((s, i) => s + i.price * i.quantity, 0);
  const count = items.reduce((s, i) => s + i.quantity, 0);
  const savedCount = savedItems.length;

  return (
    <CartContext.Provider
      value={{
        items, addItem, removeItem, updateQty, clear, subtotal, count,
        drawerOpen, setDrawerOpen,
        giftProductId, setGiftProductId,
        savedItems, savedCount, saveForLater, moveSavedToCart, removeSaved,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => useContext(CartContext);
